import { useLocalObservable } from 'mobx-react-lite';

import { getSymbolsPositions, mergeTagsInList, removeTags } from '@gem/modules/markdown';

import { ITextMessagePayload } from 'APP/model/message/messageModel.types';

export interface ILinkItemProps {
  links: string[];
  payload: ITextMessagePayload;
}

type TLinkItemPresenterLink = {
  imageUrl: string | null;
  title: string;
  description: string | null;
  links: string[];
};

interface ILinkItemPresenter {
  link: {
    imageUrl: string | null;
    title: string;
    description: string | null;
    links: string[];
  };
}

const MAX_DESCRIPTION_LENGTH = 100;

const getShortDescription = (description: string): string => {
  return description.length > MAX_DESCRIPTION_LENGTH
    ? `${description.substring(0, MAX_DESCRIPTION_LENGTH)}…`
    : description;
};

const getDomainNameFromLink = (link: string): string | null => {
  try {
    const url = new URL(link);
    return url.hostname;
  } catch (e) {
    return null;
  }
};

/**
 * Извлекает первые MAX_DESCRIPTION_LENGTH символов с начала сообщения до первой ссылки
 * @param {string} message - сообщение, в котором ищем ссылку
 * @return {string} - первые 100 символов от начала сообщения или пустая строка
 */
const getTextBeforeFirstLink = (message: string): string => {
  const { symbolsPositions } = getSymbolsPositions({ text: message });
  const positionToTag = mergeTagsInList({ symbolsPositions });
  const firstLinkContent = symbolsPositions.link?.[0]?.link;

  if (!firstLinkContent) {
    return '';
  }
  const plainText = removeTags({ text: message, positionToTag });
  if (!plainText) {
    return '';
  }
  const linkIndex = plainText.indexOf(firstLinkContent);
  if (linkIndex === -1) {
    return '';
  }
  const endIndex = Math.min(MAX_DESCRIPTION_LENGTH, linkIndex);
  return `${plainText.substring(0, endIndex)}${linkIndex > MAX_DESCRIPTION_LENGTH ? '…' : ''}`;
};

export const useLinkItemPresenter = ({ payload, links }: ILinkItemProps): ILinkItemPresenter => {
  const presenter = useLocalObservable<ILinkItemPresenter>(() => ({
    get link(): TLinkItemPresenterLink {
      const descriptionFromText = payload.text ? getTextBeforeFirstLink(payload.text) : '';
      if (payload.linkPreview) {
        const { title, domain, image, description } = payload.linkPreview;

        return {
          imageUrl: image?.url || null,
          title: title || domain,
          description: descriptionFromText || getShortDescription(description),
          links,
        };
      }

      return {
        imageUrl: null,
        title: getDomainNameFromLink(links[0]) || links[0] || '',
        description: descriptionFromText,
        links,
      };
    },
  }));

  return presenter;
};
